import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { IDevice } from '../../interfaces';
import { environment } from 'src/environment/environment';
import { Observable, Subject } from 'rxjs';
import { shakeParams } from '../../classes';
import { IListResponse } from 'src/app/core';

@Injectable({
  providedIn: 'root',
})
export class DevicesService {
  private readonly _http: HttpClient = inject(HttpClient);

  private refetch = new Subject<void>();
  public refetch$ = this.refetch.asObservable();

  private search = new Subject<string>();
  public search$ = this.search.asObservable();

  /**
   * Retrieves a list of devices from the API.
   * @param params - The query parameters to include in the request.
   * @returns An Observable that emits an IListResponse of IDevice objects.
   */
  getAll(params: any): Observable<IListResponse<IDevice>> {
    params = shakeParams(params);
    return this._http.get<IListResponse<IDevice>>(
      `${environment.apiGateway}/device`,
      {
        params,
      }
    );
  }

  /**
   * Retrieves a list of unused devices for a given doctor.
   * @param doctorId The ID of the doctor to retrieve unused devices for.
   * @returns An Observable that emits an array of IDevice objects representing the unused devices.
   */
  getUnused(doctorId?: number | undefined): Observable<IDevice[]> {
    let params = new HttpParams();
    if (doctorId) {
      params = params.append('doctorId', doctorId);
    }
    return this._http.get<IDevice[]>(
      `${environment.apiGateway}/device/unused`,
      {
        params,
      }
    );
  }

  /**
   * Creates a new device.
   * @param device The device object to create.
   * @returns An Observable that emits the response data when the request is successful.
   */
  create(device: IDevice): Observable<unknown> {
    return this._http.post<unknown>(`${environment.apiGateway}/device`, device);
  }

  /**
   * Retrieves a device by its ID from the API.
   * @param id The ID of the device to retrieve.
   * @returns An Observable that emits the retrieved device.
   */
  getById(id: number): Observable<IDevice> {
    return this._http.get<IDevice>(`${environment.apiGateway}/device/${id}`);
  }

  /**
   * Updates a device in the backend.
   * @param device The device object to update.
   * @returns An observable that resolves to an unknown value.
   */
  update(device: IDevice): Observable<unknown> {
    return this._http.put<unknown>(`${environment.apiGateway}/device`, device);
  }

  /**
   * Deletes a device with the specified ID.
   * @param id The ID of the device to delete.
   * @returns An Observable that resolves with no value when the device is successfully deleted.
   */
  delete(id: number): Observable<void> {
    return this._http.delete<void>(`${environment.apiGateway}/device/${id}`);
  }

  /**
   * Triggers a refetch of the devices data.
   */
  refetchDevices(): void {
    this.refetch.next();
  }

  onSearch(search: string): void {
    this.search.next(search);
  }
}
